import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useCallback, useMemo } from "react";

const resolveApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_BASE_URL;
  if (!configured) {
    return `${window.location.protocol}//${window.location.hostname}:9090`;
  }

  try {
    const u = new URL(configured);
    const localHosts = new Set(["127.0.0.1", "localhost"]);
    if (localHosts.has(u.hostname) && localHosts.has(window.location.hostname)) {
      u.hostname = window.location.hostname;
      return u.toString().replace(/\/$/, "");
    }
    return configured;
  } catch {
    return configured;
  }
};

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const AUTH_PAGES = new Set(["/login", "/signup", "/auth/callback"]);
const SESSION_CHECK_URLS = new Set(["/identity/me"]);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || "";
      const isSessionCheck = SESSION_CHECK_URLS.has(requestUrl);

      if (!isSessionCheck) {
        localStorage.removeItem("account_details");
        sessionStorage.removeItem("account_details");

        if (!AUTH_PAGES.has(window.location.pathname)) {
          window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        }
      }
    }
    return Promise.reject(error);
  }
);

export const useHttpAdapter = () => {
  const get = useCallback(
    async <T>(
      url: string,
      config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
      return axiosInstance.get(url, config);
    },
    []
  );

  const post = useCallback(
    async <T>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
      return axiosInstance.post(url, data, config);
    },
    []
  );

  const patch = useCallback(
    async <T>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
      return axiosInstance.request({ method: "PATCH", url, data, ...config });
    },
    []
  );

  const del = useCallback(
    async <T>(
      url: string,
      config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
      return axiosInstance.delete(url, config);
    },
    []
  );

  const put = useCallback(
    async <T>(
      url: string,
      data?: any,
      config?: AxiosRequestConfig
    ): Promise<AxiosResponse<T>> => {
      return axiosInstance.put(url, data, config);
    },
    []
  );

  return useMemo(
    () => ({ get, post, patch, del, put, axiosInstance }),
    [get, post, patch, del, put]
  );
};
